import React, { useState, useEffect, useCallback } from 'react';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { ChatSummarySection } from './ChatSummarySection';
import { SummaryContent, SECTION_TITLES, SECTION_LIMITS, TemplateSection } from '@/types/chatCompletion';
import { updateSummary, regenerateSection } from '@/api/chat';

interface ChatSummaryEditorProps {
  summaryId: number;
  initialContent: SummaryContent;
  onFinalize: () => void;
  onClose?: () => void;
}

export function ChatSummaryEditor({
  summaryId,
  initialContent,
  onFinalize,
  onClose,
}: ChatSummaryEditorProps) {
  const { toast } = useToast();
  const [content, setContent] = useState<SummaryContent>(initialContent);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [regeneratingSection, setRegeneratingSection] = useState<string | null>(null);
  const [lastSavedContent, setLastSavedContent] = useState<SummaryContent>(initialContent);

  // Auto-save functionality
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const autoSaveTimer = setTimeout(async () => {
      await handleSave(true);
    }, 3000); // Auto-save after 3 seconds of no changes

    return () => clearTimeout(autoSaveTimer);
  }, [content, hasUnsavedChanges]);

  // Save to localStorage for recovery
  useEffect(() => {
    const saveToLocalStorage = () => {
      if (hasUnsavedChanges) {
        localStorage.setItem(`chat-summary-draft-${summaryId}`, JSON.stringify(content));
      }
    };

    saveToLocalStorage();
    window.addEventListener('beforeunload', saveToLocalStorage);

    return () => {
      window.removeEventListener('beforeunload', saveToLocalStorage);
    };
  }, [summaryId, content, hasUnsavedChanges]);

  // Restore from localStorage if available
  useEffect(() => {
    const savedDraft = localStorage.getItem(`chat-summary-draft-${summaryId}`);
    if (savedDraft) {
      try {
        const parsedDraft = JSON.parse(savedDraft);
        setContent(parsedDraft);
        setHasUnsavedChanges(true);
        toast({
          title: 'Draft restored',
          description: 'We found an unsaved draft of your summary.',
        });
      } catch (error) {
        console.error('Failed to restore draft:', error);
      }
    }
  }, [summaryId, toast]);

  const handleSectionEdit = useCallback((sectionId: string, newContent: string) => {
    setContent((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionId]: newContent,
      },
    }));
    setHasUnsavedChanges(true);
  }, []);

  const handleSave = async (isAutoSave = false) => {
    setIsSaving(true);
    try {
      await updateSummary(summaryId, { content });
      setLastSavedContent(content);
      setHasUnsavedChanges(false);
      localStorage.removeItem(`chat-summary-draft-${summaryId}`);
      
      if (!isAutoSave) {
        toast({
          title: 'Summary saved',
          description: 'Your changes have been saved successfully.',
        });
      }
    } catch (error) {
      toast({
        title: 'Save failed',
        description: 'Failed to save your changes. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerateSection = async (sectionId: string) => {
    setRegeneratingSection(sectionId);
    try {
      const response = await regenerateSection(summaryId, sectionId);
      handleSectionEdit(sectionId, response.newContent);
      toast({
        title: 'Section regenerated',
        description: 'The section has been updated with new content.',
      });
    } catch (error) {
      toast({
        title: 'Regeneration failed',
        description: 'Failed to regenerate the section. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setRegeneratingSection(null);
    }
  };

  const handleFinalize = async () => {
    if (hasUnsavedChanges) {
      await handleSave();
    }
    onFinalize();
  };

  const getSectionTitle = (sectionId: string): string => {
    // Use template config if available
    if (content.template_config?.sections) {
      const section = content.template_config.sections.find(s => s.id === sectionId);
      if (section) return section.title;
    }
    // Fall back to hardcoded titles
    return SECTION_TITLES[sectionId] || sectionId;
  };

  const getSectionConfig = (sectionId: string): TemplateSection | undefined => {
    return content.template_config?.sections.find(s => s.id === sectionId);
  };

  const getSectionLimits = (sectionId: string) => {
    const sectionConfig = getSectionConfig(sectionId);
    const globalLimits = content.template_config?.sectionLimits || SECTION_LIMITS;
    
    return {
      minLength: sectionConfig?.minLength ?? globalLimits.minLength,
      maxLength: sectionConfig?.maxLength ?? globalLimits.maxLength
    };
  };

  // Get sections to display - either from template config or existing sections
  const sectionsToDisplay = content.template_config?.sections 
    ? content.template_config.sections.map(s => s.id)
    : Object.keys(content.sections);

  return (
    <div className="flex flex-col h-full max-h-[90vh] sm:max-h-[80vh]">
      <div className="flex-shrink-0 p-4 sm:p-6 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold">Conversation Summary</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {content.template_config?.introText || 'Review and edit your summary below. Changes are saved automatically.'}
            </p>
          </div>
          {hasUnsavedChanges && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Saving...</span>
            </div>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1 p-4 sm:p-6">
        <div className="space-y-4 max-w-3xl mx-auto">
          {sectionsToDisplay.map((sectionId) => {
            const sectionContent = content.sections[sectionId] || '';
            const sectionConfig = getSectionConfig(sectionId);
            const limits = getSectionLimits(sectionId);
            
            return (
              <ChatSummarySection
                key={sectionId}
                id={sectionId}
                title={getSectionTitle(sectionId)}
                content={sectionContent}
                placeholder={sectionConfig?.placeholder}
                minLength={limits.minLength}
                maxLength={limits.maxLength}
                required={sectionConfig?.required}
                onEdit={(newContent) => handleSectionEdit(sectionId, newContent)}
                onRegenerate={() => handleRegenerateSection(sectionId)}
                isRegenerating={regeneratingSection === sectionId}
              />
            );
          })}
        </div>
      </ScrollArea>

      <div className="flex-shrink-0 p-4 sm:p-6 border-t bg-background">
        <div className="flex flex-col sm:flex-row gap-3 justify-between items-center max-w-3xl mx-auto">
          <div className="text-sm text-muted-foreground">
            <span>Generated from {content.metadata.conversation_length} messages</span>
            {hasUnsavedChanges && <span className="ml-2">• Unsaved changes</span>}
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            {onClose && (
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 sm:flex-initial"
              >
                Cancel
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleSave()}
              disabled={!hasUnsavedChanges || isSaving}
              className="flex-1 sm:flex-initial"
            >
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button
              variant="default"
              onClick={handleFinalize}
              disabled={isSaving}
              className="flex-1 sm:flex-initial"
            >
              {content.template_config?.completionButtonText || 'Save & Continue'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}